
import { defineComponent, onMounted, onUnmounted, computed, ref } from "vue";
import {
  useWithdrawalRequestsStore,
  WithdrawalRequestUI,
} from "@/stores/withdrawalRequests";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import timezone from "dayjs/plugin/timezone";
import { storeToRefs } from "pinia";

dayjs.extend(timezone);
dayjs.extend(advancedFormat);

export default defineComponent({
  name: "SentWithdrawals",
  setup() {
    const store = useWithdrawalRequestsStore();
    const expanded = ref<Record<string, boolean>>({});

    const {
      sentItems: items,
      isSentLoading,
      sentPage,
      sentPageSize,
      sentHasMore,
      sentTotalCount,
      sentFilterYear,
      sentFilterMonth,
    } = storeToRefs(store);

    const years = computed(() => {
      const current = new Date().getFullYear();
      const list: number[] = [];
      for (let y = 2025; y <= current; y++) {
        list.push(y);
      }
      return list;
    });

    const months = [
      "Enero",
      "Febrero",
      "Marzo",
      "Abril",
      "Mayo",
      "Junio",
      "Julio",
      "Agosto",
      "Septiembre",
      "Octubre",
      "Noviembre",
      "Diciembre",
    ];

    const filterYear = computed({
      get: () => sentFilterYear.value,
      set: (val) => store.fetchSentRequests({ year: val, page: 0 }),
    });

    const filterMonth = computed({
      get: () => sentFilterMonth.value,
      set: (val) => store.fetchSentRequests({ month: val, page: 0 }),
    });

    const totalPages = computed(() => {
      const total = sentTotalCount.value || 0;
      const size = sentPageSize.value || 1;
      return Math.max(1, Math.ceil(total / size));
    });

    const maxPage = computed(() => totalPages.value - 1);
    const windowStart = computed(() => Math.max(0, sentPage.value - 2));
    const windowEnd = computed(() =>
      Math.min(maxPage.value, sentPage.value + 2)
    );

    const pageWindow = computed(() => {
      const pages: number[] = [];
      for (let p = windowStart.value; p <= windowEnd.value; p += 1) {
        pages.push(p);
      }
      return pages;
    });

    const showLeftEllipsis = computed(() => windowStart.value > 0);
    const showRightEllipsis = computed(() => windowEnd.value < maxPage.value);

    onMounted(() => {
      store.fetchSentRequests();
    });

    onUnmounted(() => {
      store.stopListeningSent();
    });

    const loading = computed(() => store.isLoading);

    const goToPage = (page: number) => {
      if (isSentLoading.value) return;
      store.fetchSentRequests({ page });
    };

    const nextSentPage = () => {
      goToPage(sentPage.value + 1);
    };

    const prevSentPage = () => {
      goToPage(sentPage.value - 1);
    };

    const toggleExpand = (id: string) => {
      expanded.value[id] = !expanded.value[id];
    };

    const formatDateTimezone = (date: Date | undefined) => {
      if (!date) return "-";
      return dayjs(date).format("YYYY/MM/DD HH:mm:ss z");
    };

    const formatBtc = (val: number) => {
      return val.toFixed(8) + " BTC";
    };

    const formatUsdSmall = (val: number) => {
      return (
        "$ " +
        new Intl.NumberFormat("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(val) +
        " USD"
      );
    };

    const calculateSubtotalUsd = (item: WithdrawalRequestUI) => {
      return item.destinations.reduce((acc, d) => acc + (d.amountUsd || 0), 0);
    };

    const calculateSubtotalBtc = (item: WithdrawalRequestUI) => {
      return item.destinations.reduce((acc, d) => acc + (d.amountBtc || 0), 0);
    };

    const getExplorerLink = (txId: string) => {
      // Default to Blockchair or Mempool
      return `https://blockchair.com/bitcoin/transaction/${txId}`;
    };

    const getTxStatusLabel = (txStatus?: string) => {
      if (!txStatus || txStatus === "new") return "Pendiente";
      switch (txStatus) {
        case "complete":
          return "Pagado";
        case "confirmed":
          return "Procesando";
        case "failed":
          return "Fallida";
        case "rejected":
          return "Rechazada";
        case "processing":
          return "En proceso";
        default:
          return txStatus;
      }
    };

    const getTxStatusClass = (txStatus?: string) => {
      if (!txStatus || txStatus === "new") return "bg-warning";
      switch (txStatus) {
        case "complete":
          return "bg-success";
        case "confirmed":
        case "processing":
          return "bg-info";
        case "failed":
        case "rejected":
          return "bg-danger";
        default:
          return "bg-secondary";
      }
    };

    const getTxStatusIcon = (txStatus?: string) => {
      if (!txStatus || txStatus === "new") return "far fa-clock";
      switch (txStatus) {
        case "complete":
          return "far fa-check-circle";
        case "confirmed":
        case "processing":
          return "fas fa-cogs";
        case "failed":
        case "rejected":
          return "fas fa-times-circle";
        default:
          return "fas fa-info-circle";
      }
    };

    const currencyBadgeClass = (currency: string) =>
      currency === "USD" ? "bg-success" : "bg-warning";

    return {
      items,
      loading,
      expanded,
      toggleExpand,
      formatDateTimezone,
      formatBtc,
      formatUsdSmall,
      calculateSubtotalUsd,
      calculateSubtotalBtc,
      getExplorerLink,
      getTxStatusLabel,
      getTxStatusClass,
      getTxStatusIcon,
      currencyBadgeClass,
      sentPage,
      sentTotalCount,
      totalPages,
      isSentLoading,
      sentHasMore,
      pageWindow,
      showLeftEllipsis,
      showRightEllipsis,
      goToPage,
      nextSentPage,
      prevSentPage,
      years,
      months,
      filterYear,
      filterMonth,
    };
  },
});
